import { useEffect, useState } from 'react';
import { Check, LogOut, Pencil } from 'lucide-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { DashboardPage } from '@/pages/DashboardPage';
import { LoginGate } from '@/components/LoginGate';
import { FilterProvider, useFilters } from '@/lib/filterContext';
import { FilterBar } from '@/components/FilterBar';
import { DailyRaceModal } from '@/components/DailyRaceModal';
import { GlobalLoadingBar } from '@/components/GlobalLoadingBar';
import { PeriodRange } from '@/components/PeriodRange';
import { AssistantPanel } from '@/components/AssistantPanel';
import { DASHBOARD_MODE } from '@/lib/config';
import { DashboardProvider, useDashboard } from '@/lib/dashboardContext';
import { DrilldownProvider } from '@/lib/drilldownContext';
import { getUserName, logout } from '@/services/authService';
import { ProcurementPage } from '@/features/procurement';

export default function App() {
  // Modo Compras/Gastos (#/compras): UI propia (board), no el grid de ventas →
  // no necesita FilterProvider/DashboardProvider/DrilldownProvider.
  if (DASHBOARD_MODE === 'compras') {
    return (
      <QueryClientProvider client={queryClient}>
        <LoginGate>
          <ComprasShell />
        </LoginGate>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <LoginGate>
        <FilterProvider>
          <DashboardProvider>
            <DrilldownProvider>
              <Shell />
            </DrilldownProvider>
          </DashboardProvider>
        </FilterProvider>
      </LoginGate>
    </QueryClientProvider>
  );
}

/** Shell mínimo del modo Compras: header de marca + el board del slice. */
function ComprasShell() {
  const user = getUserName();
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-to-r from-[#5c1212] via-[#7d1818] to-[#a52121] px-6 py-3 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="whitespace-nowrap text-lg font-black tracking-tight">
              Compras y Gastos — Salo&amp;Gaash
            </h1>
            <p className="text-xs font-medium text-white/70">{user}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-emerald-500 text-sm font-bold text-white shadow">
              {initials(user)}
            </span>
            <button
              onClick={() => {
                logout();
                window.location.reload();
              }}
              title="Salir"
              className="grid h-9 w-9 place-items-center rounded-lg bg-red-900/40 text-white hover:bg-red-900/60"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>
      {/* La página trae su propia barra de filtros a lo ancho + <main>. */}
      <ProcurementPage />
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

function Shell() {
  const { editMode, setEditMode } = useDashboard();
  const { companies } = useFilters();
  const user = getUserName();

  // Carrera del Día: VG/Piélé = caballos; SmartBamboo = ajedrez. El botón vive en la
  // FilterBar; aquí está el estado y el modal. Salta sola 1 vez al día (flag por fecha).
  const [raceOpen, setRaceOpen] = useState(false);
  const raceApplies = companies.length > 0;
  useEffect(() => {
    if (!raceApplies) return;
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem('race_seen') !== today) {
      localStorage.setItem('race_seen', today);
      setRaceOpen(true);
    }
  }, [raceApplies]);

  return (
    <div className="min-h-screen overflow-x-clip bg-slate-50">
      <GlobalLoadingBar />
      <header className="bg-gradient-to-r from-[#5c1212] via-[#7d1818] to-[#a52121] px-6 py-3 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="whitespace-nowrap text-lg font-black tracking-tight">Dashboard - Salo&amp;Gaash</h1>
            <p className="text-xs font-medium text-white/70">
              {user} · <PeriodRange />
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setEditMode(!editMode)}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                editMode ? 'bg-white text-red-700' : 'bg-white/15 hover:bg-white/25'
              }`}
            >
              {editMode ? <Check className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
              {editMode ? 'Listo' : 'Editar'}
            </button>
            <span className="grid h-9 w-9 place-items-center rounded-full bg-emerald-500 text-sm font-bold text-white shadow">
              {initials(user)}
            </span>
            <button
              onClick={() => {
                logout();
                window.location.reload();
              }}
              title="Salir"
              className="grid h-9 w-9 place-items-center rounded-lg bg-red-900/40 text-white hover:bg-red-900/60"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>
      <FilterBar onOpenRace={raceApplies ? () => setRaceOpen(true) : undefined} />
      <main className="p-4">
        <DashboardPage />
      </main>
      <DailyRaceModal open={raceOpen} onClose={() => setRaceOpen(false)} />
      {/* Cada dashboard usa su propio asistente de Odoo: logística → 'dashlog'
          (experto en inventario/resurtido); comercio → 'dashboard' (default). */}
      <AssistantPanel assistantCode={DASHBOARD_MODE === 'logistica' ? 'dashlog' : 'dashboard'} />
    </div>
  );
}
